// Pillow 3D Controller (v2 — used by pillow-3d-viewer-v2)
// - Binds <pillow-3d> success/error events
// - Provides small helpers to update attributes (size/color/src/etc.)
//
// Theme pattern: "component-*.js" for snippet/component scripts.

(() => {
  'use strict';

  const PILLOW_3D_SELECTOR = 'pillow-3d';
  const ALLOWED_SIZES = new Set([10, 16, 22, 30, 60]);

  let isBound = false; 

  // ============================================================================
  // Image Generator Module
  // ============================================================================
  const ImageGenerator = (() => {
    'use strict';

    const TRIM_SCAN_MAX_SIZE = 800;
    const ALPHA_THRESHOLD = 150;
    const PREVIEW_CANVAS_SIZE = 500;
    const PREVIEW_PADDING = 50;
    const PREVIEW_BG_COLOR = '#e1cff2';

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  }

  function canvasToBlob(canvas, mimeType, quality) {
    if (canvas.convertToBlob) {
      return canvas.convertToBlob({ type: mimeType, quality: quality });
    }
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      }, mimeType, quality);
    });
  }

  function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
      reader.readAsDataURL(blob);
    });
  }

  function shouldSkipTrimming(imageUrl) {
    const url = imageUrl.toLowerCase();
    return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.includes('data:image/jpeg');
  }

    // Free canvas memory
    function disposeCanvas(canvas, ctx) {
      if (!canvas) return;
      
      try {
        if (ctx && typeof ctx.clearRect === 'function') {
          ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
        }
        if (canvas instanceof HTMLCanvasElement) {
          canvas.width = 0;
          canvas.height = 0;
        }
      } catch (error) {
        // Ignore errors
      }
    }

    // Convert image to blob (auto-cleanup)
    async function imageToBlob(img, options = {}) {
      const {
        width = img.width,
        height = img.height,
        alpha = true
      } = options;

        const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha });
      
      try {
        ctx.drawImage(img, 0, 0, width, height);
        return await canvasToBlob(canvas, 'image/png');
      } finally {
        disposeCanvas(canvas, ctx);
      }
    }

    // Create scan canvas (uses OffscreenCanvas if available)
    function createScanCanvas(width, height) {
      const useOffscreen = typeof OffscreenCanvas !== 'undefined';
      
      if (useOffscreen) {
        const canvas = new OffscreenCanvas(width, height);
        const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
        return { canvas, ctx };
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      return { canvas, ctx };
    }

    async function trimTransparentPixelsToBlob(imageUrl) {
      let scanCanvas = null;
      let scanCtx = null;
      let out = null;
      let outCtx = null;
      
      try {
      const img = await loadImage(imageUrl);
      
        // Skip trimming for JPEG
        if (shouldSkipTrimming(imageUrl)) {
          return await imageToBlob(img);
        }

        // Downscale large images for faster scanning
      const maxDim = Math.max(img.width, img.height);
      const needsDownscale = maxDim > TRIM_SCAN_MAX_SIZE;
      const scale = needsDownscale ? TRIM_SCAN_MAX_SIZE / maxDim : 1;
      const scanWidth = Math.round(img.width * scale);
      const scanHeight = Math.round(img.height * scale);

        // Scan for transparent pixels
        const scanResult = createScanCanvas(scanWidth, scanHeight);
        scanCanvas = scanResult.canvas;
        scanCtx = scanResult.ctx;
      scanCtx.drawImage(img, 0, 0, scanWidth, scanHeight);

        let imageData;
      try {
          imageData = scanCtx.getImageData(0, 0, scanWidth, scanHeight);
      } catch (error) {
          disposeCanvas(scanCanvas, scanCtx);
          scanCanvas = null;
          scanCtx = null;
          
        if (error.name === 'SecurityError') {
            return await imageToBlob(img);
        }
        throw error;
      }

        // Find bounding box
      const { data, width, height } = imageData;
      const stride = width * 4;
      let minX = width, minY = height, maxX = -1, maxY = -1;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = data[y * stride + x * 4 + 3];
          if (alpha > ALPHA_THRESHOLD) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

        disposeCanvas(scanCanvas, scanCtx);
        scanCanvas = null;
        scanCtx = null;

        // No opaque pixels found
      if (maxX < 0) {
          return await imageToBlob(img);
      }

        // Calculate trim bounds
      const w = maxX - minX + 1;
      const h = maxY - minY + 1;
      const origMinX = Math.round(minX / scale);
      const origMinY = Math.round(minY / scale);
      const origW = Math.round(w / scale);
      const origH = Math.round(h / scale);

        // Create trimmed canvas
        out = document.createElement('canvas');
      out.width = origW;
      out.height = origH;
        outCtx = out.getContext('2d', { 
        alpha: true,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });

      outCtx.drawImage(img, origMinX, origMinY, origW, origH, 0, 0, origW, origH);

        const blob = await canvasToBlob(out, 'image/png');
        disposeCanvas(out, outCtx);
        return blob;
    } catch (error) {
        if (scanCanvas) disposeCanvas(scanCanvas, scanCtx);
        if (out) disposeCanvas(out, outCtx);
        
        // Fallback for security errors
      if (error.name === 'SecurityError' || error.message.includes('tainted')) {
        const img = await loadImage(imageUrl);
          return await imageToBlob(img);
      }
      throw error;
    }
  }
  
  async function createPreviewWithBackground(blob) {
      let canvas = null;
      let ctx = null;
      
      try {
        // Load image (use ImageBitmap if available)
        const img = typeof createImageBitmap !== 'undefined' 
          ? await createImageBitmap(blob)
          : await loadImage(await blobToDataURL(blob));
        
        // Calculate fit scale
        const imageArea = PREVIEW_CANVAS_SIZE - (PREVIEW_PADDING * 2);
      const scale = Math.min(imageArea / img.width, imageArea / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
        const x = PREVIEW_PADDING + (imageArea - scaledWidth) / 2;
        const y = PREVIEW_PADDING + (imageArea - scaledHeight) / 2;
      
        // Draw on canvas
        canvas = document.createElement('canvas');
        canvas.width = PREVIEW_CANVAS_SIZE;
        canvas.height = PREVIEW_CANVAS_SIZE;
        ctx = canvas.getContext('2d');
      
        ctx.fillStyle = PREVIEW_BG_COLOR;
        ctx.fillRect(0, 0, PREVIEW_CANVAS_SIZE, PREVIEW_CANVAS_SIZE);
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      
        const resultDataUrl = canvas.toDataURL('image/png');
        disposeCanvas(canvas, ctx);
        if (img.close) img.close();
        
        return resultDataUrl;
    } catch (error) {
        if (canvas) disposeCanvas(canvas, ctx);
      console.error('[Pillow3D] Failed to create preview with background:', error);
      return null;
    }
  }

  async function processPreviewImage(imageUrl) {
    const trimmedBlob = await trimTransparentPixelsToBlob(imageUrl);
    const previewNoBg = await blobToDataURL(trimmedBlob);
    const previewWithBg = await createPreviewWithBackground(trimmedBlob);

    return { 
      previewNoBg,
      previewWithBg: previewWithBg || null
    };
  }

    return {
      loadImage,
      canvasToBlob,
      blobToDataURL,
      imageToBlob,
      trimTransparentPixelsToBlob,
      createPreviewWithBackground,
      processPreviewImage
    };
  })();

  // ============================================================================
  // Pillow3D Module
  // ============================================================================
  const Pillow3D = (() => {
    'use strict';

    function getEl() {
      return $(PILLOW_3D_SELECTOR).get(0) || null;
    }


  function attach(el) {
    if (!el || isBound) return;
    isBound = true;

    const $el = $(el);

    function getOptions() {
      const optionsAttr = $el.attr('data-task-options');
      if (!optionsAttr) return null;
      try {
        return JSON.parse(optionsAttr);
      } catch (e) {
        return null;
      }
    }

    /** Viewer attr source → contract: generate | restore | props */
    function toContractSource(viewerSource) {
      if (viewerSource === 'colorbg') return 'props';
      if (viewerSource === 'pillowdata') return 'restore';
      if (viewerSource === 'src') return 'generate';
      return viewerSource || 'generate';
    }

    function getSuccessSource(detail) {
      return detail?.data?.source || detail?.source || null;
    }

    function getGeneration() {
      return getOptions()?.generation ?? null;
    }

    function dispatchMockup(name, detail) {
      el.dispatchEvent(new CustomEvent(`mockup:${name}`, {
        bubbles: true,
        detail: detail || {}
      }));
    }

    $(el).on('error', (e) => {
      const original = e?.originalEvent || e;
      const raw = original?.detail || {};
      const message = raw?.error?.message || raw?.message || null;

      // Do not clear src — would wipe pillowdata / remount viewer (same as abort)
      dispatchMockup('error', {
        type: raw?.type || null,
        message,
        generation: getGeneration()
      });
    });

    $(el).on('success', async (e) => {
      const original = e?.originalEvent || e;
      const raw = original?.detail || {};
      const data = raw.data ? { ...raw.data } : { ...raw };

      if (!data && !raw) return;

      let preview = data.preview || null;
      let previewWithBg = data.previewWithBg || null;

      if (preview) {
        try {
          const processedImages = await ImageGenerator.processPreviewImage(preview);
          if (processedImages) {
            preview = processedImages.previewNoBg;
            if (processedImages.previewWithBg) {
              previewWithBg = processedImages.previewWithBg;
            }
          }
        } catch (error) {
          if (error.name !== 'SecurityError') {
            console.error('Error processing preview image:', error);
          }
        }
      }

      // mockups.main: noBg → picker; withBg → form / history (orchestrator contract)
      dispatchMockup('success', {
        mockups: {
          main: {
            noBg: preview || null,
            withBg: previewWithBg || null
          }
        },
        removebg: { main: data.removedBackgroundImage},
        pillowData: data.pillowData || null,
        source: toContractSource(getSuccessSource(raw)),
        generation: getGeneration()
      });
    });
  }

  function dispatchMockupOnEl(name, detail) {
    const el = getEl();
    if (!el) return;
    el.dispatchEvent(new CustomEvent(`mockup:${name}`, {
      bubbles: true,
      detail: detail || {}
    }));
  }

  // Set multiple attributes at once.
  function setState(state) {
    const el = getEl();
    if (!el || !state) return;

    if ('src' in state) {
      const src = state.src;
      const currentSrc = $(el).attr('src');

      if (src && src !== currentSrc) {
        let generation = null;
        try {
          const optionsAttr = $(el).attr('data-task-options');
          if (optionsAttr) generation = JSON.parse(optionsAttr)?.generation ?? null;
        } catch (_) { /* ignore */ }

        dispatchMockupOnEl('start', { source: 'generate', generation });
      }

      if (src) $(el).attr('src', src);
      else $(el).attr('src', '');
    }

    if ('pillowData' in state) {
      const pillowData = state.pillowData;
      if (pillowData) $(el).attr('pillowdata', pillowData);
      else $(el).attr('pillowdata', '');
    }

    if ('color' in state) {
      const color = state.color;
      if (color) {
        $(el).attr('colorbg', color);
      }
    }
  }

    function setSize(value) {
      const el = getEl();
      if (!el) return;

      const size = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
      if (!Number.isFinite(size) || !ALLOWED_SIZES.has(size)) return;

      $(el).attr('size', size);
  }

  function setColor(color) {
    setState({ color });
  }

  function setSrc(src) {
    setState({ src });
  }

  function setPillowData(pillowData, color) {
    if (!color) {
      throw new Error('setPillowData: color parameter is required');
    }
    setState({ pillowData, color });
  }

  function setJobGeneration(generation) {
    const el = getEl();
    if (!el || generation == null) return;
    $(el).attr('data-task-options', JSON.stringify({ generation }));
  }

  /**
   * Implements Mockup Engine contract — see custom-product-orchestrator-v2.js header.
   *
   * process({ sources?, pillowData?, props?, generation? })
   * Emits mockup:start|success|error (bubbles).
   * success: { mockups: { main: { noBg, withBg } }, pillowData, generation }
   */
  function process(opts = {}) {
    const el = getEl();
    if (!el) {
      throw new Error('Pillow3D.process: element not found');
    }

    const props = opts.props && typeof opts.props === 'object' ? opts.props : {};
    const color = props.color || null;
    const mainFromSources = opts.sources?.main;
    const dataUrl = (typeof mainFromSources === 'string' ? mainFromSources : mainFromSources?.dataUrl)
      || null;
    const pillowData = opts.pillowData != null ? opts.pillowData : null;
    const generation = opts.generation ?? null;

    setJobGeneration(opts.generation);

    // Props-only (e.g. color change) — reuse current viewer restore data
    if (!dataUrl && pillowData == null) {
      if (!color) {
        throw new Error('Pillow3D.process: props.color is required when sources/pillowData are missing');
      }
      dispatchMockupOnEl('start', { source: 'props', generation });
      setColor(color);
      if (props.size != null) setSize(props.size);
      return { mode: 'props' };
    }

    if (dataUrl) {
      const next = { src: dataUrl };
      if (color) next.color = color;
      // Keep setSrc timing stable for viewer (was rAF in section adapter)
      // mockup:start fires from setState when src actually changes
      requestAnimationFrame(() => {
        setState(next);
        if (props.size != null) setSize(props.size);
      });
      return { mode: 'source' };
    }

    if (pillowData) {
      if (!color) {
        throw new Error('Pillow3D.process: props.color is required with pillowData');
      }
      dispatchMockupOnEl('start', { source: 'restore', generation });
      setPillowData(pillowData, color);
      if (props.size != null) setSize(props.size);
      return { mode: 'restore' };
    }

    throw new Error('Pillow3D.process: sources.main or pillowData is required');
  }

  function abort() {
    // Do not clear src. Viewer attributeChangedCallback on src='' nulls pillowdata and
    // remounts the 3D app; stop() then hits null frameDispatcher
    // ("Cannot read properties of null (reading 'stop')"), and color change after
    // upload loses pillowdata. Stale viewer success is ignored via job generation.
  }

  // Module internals — orchestrator should only use process / getEl / abort
  return {
      attach,
      getEl,
      process,
      abort,
      // kept for internal use / rare direct viewer helpers
      setState,
      setSize
    };
  })();

  // Initialize
  const init = () => {
    Pillow3D.attach(Pillow3D.getEl());
  };

  $(init);

  /**
   * Shaped <pillow-3d> engine — implements orchestrator Mockup Engine contract
   * (see custom-product-orchestrator-v2.js). Wired via boot: getEngine → this object.
   */
  window.ShapedPillowEngine = {
    id: 'shaped-pillow',
    target: PILLOW_3D_SELECTOR,
    getEl: () => Pillow3D.getEl(),
    process: (opts) => Pillow3D.process(opts),
    abort: () => Pillow3D.abort()
  };
})();

